import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ThumbsUp, ThumbsDown } from 'lucide-react'
import { tracks } from '../api/client'

interface LikeButtonProps {
  trackId: string
  initialLiked?: boolean | null
  size?: number
}

export default function LikeButton({ trackId, initialLiked, size = 16 }: LikeButtonProps) {
  const queryClient = useQueryClient()

  // Fetch current like status if not provided
  const { data: likeStatus } = useQuery({
    queryKey: ['track-like', trackId],
    queryFn: () => tracks.getLikeStatus(trackId),
    enabled: initialLiked === undefined,
    staleTime: 30000, // Cache for 30 seconds to avoid too many requests
  })

  // Use initialLiked if provided, otherwise use fetched status
  const liked = initialLiked !== undefined ? initialLiked : (likeStatus?.liked ?? null)

  const likeMutation = useMutation({
    mutationFn: (newLiked: boolean | null) => tracks.setLike(trackId, newLiked),
    onSuccess: (_, newLiked) => {
      // Update the cache immediately
      queryClient.setQueryData(['track-like', trackId], { liked: newLiked })
      // Invalidate liked tracks lists to refresh them
      queryClient.invalidateQueries({ queryKey: ['liked-tracks'] })
      queryClient.invalidateQueries({ queryKey: ['tracks', 'liked'] })
    },
  })

  const handleLike = (e: React.MouseEvent) => {
    e.stopPropagation()
    const newValue = liked === true ? null : true
    likeMutation.mutate(newValue)
  }

  const handleDislike = (e: React.MouseEvent) => {
    e.stopPropagation()
    const newValue = liked === false ? null : false
    likeMutation.mutate(newValue)
  }

  return (
    <div className="flex items-center gap-1">
      <button
        onClick={handleLike}
        className={`p-1 rounded hover:bg-dark-600 transition-colors ${
          liked === true ? 'text-primary-400' : 'text-dark-400 hover:text-dark-200'
        }`}
        title="Like"
      >
        <ThumbsUp size={size} fill={liked === true ? 'currentColor' : 'none'} />
      </button>
      <button
        onClick={handleDislike}
        className={`p-1 rounded hover:bg-dark-600 transition-colors ${
          liked === false ? 'text-red-400' : 'text-dark-400 hover:text-dark-200'
        }`}
        title="Dislike"
      >
        <ThumbsDown size={size} fill={liked === false ? 'currentColor' : 'none'} />
      </button>
    </div>
  )
}
